import React, { useState, useEffect, useMemo } from 'react';
import PathwayDisclaimer from '../components/PathwayDisclaimer';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Loader2, Sparkles, ChevronRight, ChevronLeft, RotateCcw, ArrowRight, Trophy, Target } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { getVis } from './AIPathwayPage';
import { loadContent } from '../lib/contentLoader';

const AICareerQuiz = () => {
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    loadContent('ai_careers.json')
      .then((d) => {
        if (d && !d.__locked) setPkg(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const sections = pkg?.career_quiz?.sections || [];
  const scoring = pkg?.career_quiz?.scoring_model || {};

  // Tokenize selected options into a flat lowercased set for matching against role.quizSignals.
  const selectedTokens = useMemo(() => {
    const tokens = new Set();
    Object.values(answers).forEach(arr => (arr || []).forEach(opt => {
      tokens.add(opt.toLowerCase());
      opt.toLowerCase().split(/\s+/).forEach(w => { if (w.length > 2) tokens.add(w); });
    }));
    return tokens;
  }, [answers]);

  // Score each role against selected tokens.
  const ranked = useMemo(() => {
    if (!pkg) return [];
    const scored = [];
    pkg.departments.forEach(dept => {
      const deptName = dept.name || dept.department;
      dept.roles.forEach(role => {
        if (role.quizEligible === false) return; // added roles 126–135 are not quiz-eligible (bundle rule)
        const sig = role.quizSignals || {};
        let score = 0;
        const matches = [];

        // Identity / strengths
        (sig.recommended_archetypes || []).forEach(a => {
          if (selectedTokens.has(a.toLowerCase())) { score += scoring.strength_weight || 25; matches.push(a); }
        });
        // Interests
        (sig.strong_interest_matches || []).forEach(i => {
          const il = i.toLowerCase();
          if (selectedTokens.has(il) || [...selectedTokens].some(t => il.includes(t) || t.includes(il))) {
            score += scoring.interest_weight || 25;
            matches.push(i);
          }
        });
        // Goal alignment (sometimes present)
        (sig.aligned_goals || []).forEach(g => {
          if (selectedTokens.has(g.toLowerCase())) { score += scoring.goal_alignment_weight || 15; matches.push(g); }
        });

        // Generic content match against summary + careerFit
        const fitTokens = [...(role.careerFit?.bestFor || []), ...(role.careerFit?.interests || [])].map(t => t.toLowerCase());
        fitTokens.forEach(t => { if (selectedTokens.has(t)) score += (scoring.skill_confidence_weight || 20) * 0.4; });

        if (score > 0) scored.push({ deptName, role, score, matches });
      });
    });
    return scored.sort((a, b) => b.score - a.score);
  }, [pkg, selectedTokens, scoring]);

  const topDepts = useMemo(() => {
    if (!ranked.length) return [];
    const agg = {};
    ranked.forEach(r => {
      agg[r.deptName] = (agg[r.deptName] || 0) + r.score;
    });
    return Object.entries(agg).sort((a, b) => b[1] - a[1]).slice(0, 3);
  }, [ranked]);

  const topRoles = useMemo(() => ranked.slice(0, 6), [ranked]);

  const toggle = (sectionId, opt) => {
    setAnswers(prev => {
      const list = prev[sectionId] || [];
      const next = list.includes(opt) ? list.filter(o => o !== opt) : [...list, opt];
      return { ...prev, [sectionId]: next };
    });
  };

  const reset = () => { setAnswers({}); setStep(0); setSubmitted(false); window.scrollTo(0, 0); };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="animate-spin text-teal-600" size={32} /></div>;
  if (!pkg) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><p className="text-gray-700">Could not load quiz.</p></div>;

  const section = sections[step];
  const selected = answers[section?.id] || [];
  const progress = ((step + 1) / sections.length) * 100;

  // ─── Result screen ───
  if (submitted) {
    const maxScore = ranked[0]?.score || 1;
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <section className="pt-24 pb-10 px-4 md:px-6 bg-gradient-to-br from-teal-600 via-teal-700 to-blue-700 text-white">
          <div className="max-w-4xl mx-auto">
            <Link to="/explore/ai" data-testid="quiz-back-btn" className="inline-flex items-center gap-1.5 text-xs text-white/85 hover:text-white mb-4 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full">
              <ArrowLeft size={13} /> Back to AI Pathway
            </Link>
            <Badge className="bg-white text-teal-700 border-0 font-bold mb-2">Career Fit Result</Badge>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Your Top AI Career Matches</h1>
            <p className="text-white/85 text-sm md:text-base">Based on your {Object.values(answers).flat().length} selections across 5 sections</p>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 space-y-8">
          {ranked.length === 0 ? (
            <div className="p-8 bg-white rounded-2xl border border-gray-200 text-center" data-testid="no-match">
              <Sparkles className="text-amber-500 mx-auto mb-3" size={36} />
              <p className="text-base font-bold text-gray-900 mb-2">No strong matches yet</p>
              <p className="text-sm text-gray-600 mb-4">Try selecting more options across the sections — the more you tell us, the better the match.</p>
              <Button onClick={reset} data-testid="quiz-retake-btn"><RotateCcw size={14} className="mr-1.5" />Retake</Button>
            </div>
          ) : (
            <>
              {/* Top departments */}
              <section data-testid="top-depts">
                <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2"><Trophy className="text-amber-500" size={18} />Top Department Matches</h2>
                <div className="grid sm:grid-cols-3 gap-3">
                  {topDepts.map(([name, score], i) => {
                    const v = getVis(name);
                    const Icon = v.icon;
                    return (
                      <div key={name} className={`p-4 ${v.bg} rounded-2xl border border-gray-200`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`w-8 h-8 rounded-lg ${v.solid} text-white flex items-center justify-center text-xs font-bold`}>#{i + 1}</span>
                          <Icon size={16} className={v.text} />
                        </div>
                        <p className={`text-sm font-bold ${v.text} mb-1`}>{name}</p>
                        <p className="text-[10px] text-gray-500 font-mono">match score: {Math.round(score)}</p>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Top role recommendations */}
              <section data-testid="top-roles">
                <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2"><Target className="text-teal-600" size={18} />Top Role Recommendations</h2>
                <div className="space-y-2">
                  {topRoles.map((r, i) => {
                    const v = getVis(r.deptName);
                    return (
                      <Link key={r.role.id} to={`/explore/ai/role/${r.role.id}`} data-testid={`top-role-${r.role.id}`} className="block p-4 bg-white rounded-2xl border border-gray-200 hover:border-teal-400 hover:shadow-md transition-all">
                        <div className="flex items-start gap-3">
                          <span className={`flex-shrink-0 w-9 h-9 rounded-lg ${v.bg} ${v.text} flex items-center justify-center text-xs font-bold`}>#{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <p className="text-base font-bold text-gray-900 truncate">{r.role.title}</p>
                              <Badge className={`${v.bg} ${v.text} border-0 text-[10px] flex-shrink-0`}>{Math.round((r.score / maxScore) * 100)}% match</Badge>
                            </div>
                            <p className="text-[10px] text-gray-500 mb-2">{r.deptName}</p>
                            <p className="text-xs text-gray-600 line-clamp-2">{r.role.summary}</p>
                            {r.matches.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {r.matches.slice(0, 4).map((m, k) => <Badge key={k} className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px]">{m}</Badge>)}
                              </div>
                            )}
                          </div>
                          <ArrowRight size={14} className="text-gray-400 mt-1 flex-shrink-0" />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </section>

              {/* Output card */}
              <div className="p-5 bg-gradient-to-br from-teal-50 to-blue-50 border border-teal-200 rounded-2xl">
                <p className="text-xs font-bold text-teal-700 uppercase tracking-wider mb-2">What you got</p>
                <ul className="text-sm text-gray-700 space-y-1">
                  {(pkg.career_quiz.output_includes || []).map((o, i) => <li key={i} className="flex items-start gap-1.5"><ChevronRight size={12} className="mt-1 text-teal-600 flex-shrink-0" />{o}</li>)}
                </ul>
              </div>

              <div className="flex gap-3">
                <Button onClick={reset} variant="outline" className="flex-1" data-testid="retake-bottom"><RotateCcw size={14} className="mr-1.5" />Retake Quiz</Button>
                <Button asChild className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"><Link to="/explore/ai">Browse All Departments</Link></Button>
              </div>
            </>
          )}
          <PathwayDisclaimer />
        </div>
        <Footer />
      </div>
    );
  }

  // ─── Quiz screen ───
  const allAnswered = sections.every(s => (answers[s.id] || []).length > 0);
  const sectionAnswered = selected.length > 0;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="ai-quiz-page">
      <Navbar />

      {/* Progress */}
      <div className="bg-white shadow-sm px-4 py-3 lxt-sticky-nav z-20">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <Link to="/explore/ai" data-testid="quiz-exit-btn" className="text-xs text-gray-600 hover:text-gray-900 inline-flex items-center gap-1.5">
              <ArrowLeft size={13} />Exit
            </Link>
            <p className="text-xs font-semibold text-gray-700">Section {step + 1} of {sections.length}</p>
            <span className="w-9" />
          </div>
          <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <motion.div className="h-full bg-teal-600 rounded-full" initial={false} animate={{ width: `${progress}%` }} transition={{ duration: 0.3 }} />
          </div>
        </div>
      </div>

      <main className="flex-1 px-4 md:px-6 py-8">
        <div className="max-w-3xl mx-auto">
          <Badge className="bg-teal-100 text-teal-700 mb-2 text-[10px]">{section.id.replace(/_/g, ' ').toUpperCase()}</Badge>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-testid="quiz-section-title">{section.title}</h1>
          <p className="text-sm text-gray-600 mb-6">Select all that apply — multiple choices welcome</p>

          <div className="flex flex-wrap gap-2 mb-6" data-testid="quiz-options">
            {section.options.map(opt => {
              const isSel = selected.includes(opt);
              return (
                <button
                  key={opt}
                  data-testid={`quiz-opt-${opt.replace(/\s+/g, '-').toLowerCase()}`}
                  onClick={() => toggle(section.id, opt)}
                  className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-all ${
                    isSel ? 'bg-teal-600 border-teal-600 text-white shadow-md' : 'bg-white border-gray-300 text-gray-700 hover:border-teal-400'
                  }`}
                >{opt}</button>
              );
            })}
          </div>

          <p className="text-xs text-gray-500 mb-6" data-testid="selected-count">
            {selected.length} of {section.options.length} selected in this section · {Object.values(answers).flat().length} total
          </p>

          <div className="flex gap-3">
            <Button data-testid="quiz-prev-btn" variant="outline" disabled={step === 0} onClick={() => setStep(s => s - 1)} className="flex-1">
              <ChevronLeft size={14} className="mr-1.5" />Previous
            </Button>
            {step < sections.length - 1 ? (
              <Button data-testid="quiz-next-btn" disabled={!sectionAnswered} onClick={() => setStep(s => s + 1)} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-40">
                Next<ChevronRight size={14} className="ml-1.5" />
              </Button>
            ) : (
              <Button data-testid="quiz-submit-btn" disabled={!allAnswered} onClick={() => { setSubmitted(true); window.scrollTo(0, 0); }} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-40">
                <Sparkles size={14} className="mr-1.5" />See My Matches
              </Button>
            )}
          </div>
          {!allAnswered && step === sections.length - 1 && (
            <p className="text-xs text-amber-600 mt-2 text-center">Pick at least one option in each section to see your matches.</p>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AICareerQuiz;
